import { STEP } from "#src/constants/step.ts";
import {
  defineStore,
  generateInitialState,
  resetState,
  type ToSetupState,
} from "#src/stores/state-wrapper.ts";
import { ROLE_TYPE } from "#src/types/Role.ts";
import {
  type DataRequirements,
  REQS,
} from "#src/constants/data-requirements.ts";

import {
  getPartyStore,
  useEappStore,
} from "#src/stores/electronic-application.ts";
import { usePrimaryInsuredStore } from "#src/stores/insured.ts";
import { useStepBeneficiaryPrimaryStore } from "#src/stores/step-beneficiary-primary.ts";
import { useStepPersonalStore } from "#src/stores/step-personal.ts";
import { type Pinia, type StoreGeneric } from "pinia";
import { computed, type ComputedRef } from "vue";
import {
  type BaseStepStoreState,
  type BaseStepStore,
} from "#src/types/StepStore.ts";

function initialState(): BaseStepStoreState {
  return {
    inactive: false,
  };
}

interface ContingentBeneficiaryStep
  extends BaseStepStore,
    ToSetupState<BaseStepStoreState> {
  setStepRequirements(srq: DataRequirements): void;
  requiredFields: ComputedRef<Record<string, boolean>>;
}

function initializeContingentBeneficiaryStep(
  pinia: Pinia
): ContingentBeneficiaryStep {
  const state = generateInitialState<BaseStepStoreState>(initialState());

  function $reset() {
    resetState<BaseStepStoreState>(state, initialState());
  }

  const step = computed(() => STEP.CONTINGENT_BENEFICIARY);
  const requiredFields = computed(() => {
    const primary = useStepBeneficiaryPrimaryStore(pinia);
    return primary.requiredFields;
  });
  const next = computed(() => {
    const personal = useStepPersonalStore(pinia);
    if (personal.inactive) return personal.next;
    return personal.step;
  });
  const previous = computed(() => {
    const primary = useStepBeneficiaryPrimaryStore(pinia);
    if (primary.inactive) return primary.previous;
    return primary.step;
  });
  const skippable = computed(() => {
    const eApp = useEappStore(pinia);
    if (eApp.isDualInsured) return true;
    const insured = usePrimaryInsuredStore(pinia);

    if (!eApp.beneficiariesByInsuredId[insured.id]?.length) return true;

    const contingentBeneficiaries = eApp.beneficiariesByInsuredId[
      insured.id
    ].filter(
      (v: string) =>
        getPartyStore(v, pinia).roles[ROLE_TYPE.CONTINGENT_BENEFICIARY]
    );

    return !contingentBeneficiaries.length;
  });

  function setStepRequirements(srq: DataRequirements) {
    state.inactive.value = !srq[REQS.CONTINGENT_BENEFICIARY];
  }

  return {
    ...state,
    $reset,
    step,
    requiredFields,
    next,
    previous,
    skippable,
    setStepRequirements,
  };
}

export const useStepBeneficiaryContingentStore = (
  pinia: Pinia,
  hot?: StoreGeneric
) => {
  return defineStore<string, ContingentBeneficiaryStep>(
    "step-beneficiary-contingent",
    () => {
      return initializeContingentBeneficiaryStep(pinia);
    }
  )(pinia, hot);
};
